import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fdf9 0%, #e8f5e9 100%);
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Breadcrumbs = styled.nav`
  margin-bottom: 30px;
  font-size: 14px;
  color: #666;

  a {
    color: #22C55E;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }

  span {
    margin: 0 8px;
    color: #999;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
`;

const AppBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #22C55E;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #1a1a1a;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  font-size: 14px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 40px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const TableOfContents = styled.div`
  background: #f8fdf9;
  border-left: 4px solid #22C55E;
  padding: 20px 25px;
  border-radius: 0 8px 8px 0;
  margin-bottom: 30px;

  h3 {
    color: #1a1a1a;
    margin-bottom: 15px;
    font-size: 1.1rem;
  }

  ol {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: #22C55E;
    text-decoration: none;
    font-size: 15px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Section = styled.section`
  margin-bottom: 35px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  color: #1a1a1a;
  font-size: 1.4rem;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e8f5e9;
  display: flex;
  align-items: center;
  gap: 10px;

  .icon {
    color: #22C55E;
  }
`;

const Paragraph = styled.p`
  color: #444;
  line-height: 1.8;
  margin-bottom: 15px;
  font-size: 15px;
`;

const List = styled.ul`
  color: #444;
  line-height: 1.8;
  margin-bottom: 15px;
  padding-left: 25px;

  li {
    margin-bottom: 10px;
    font-size: 15px;
  }
`;

const DataTable = styled.div`
  overflow-x: auto;
  margin: 20px 0;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    th, td {
      padding: 12px 15px;
      text-align: left;
      border-bottom: 1px solid #e8f5e9;
    }

    th {
      background: #f0fdf4;
      color: #1a1a1a;
      font-weight: 600;
    }

    td {
      color: #444;
    }

    tr:hover td {
      background: #f8fdf9;
    }
  }
`;

const HighlightBox = styled.div`
  background: ${props => props.variant === 'warning' ? '#fef3c7' : '#f0fdf4'};
  border-left: 4px solid ${props => props.variant === 'warning' ? '#f59e0b' : '#22C55E'};
  padding: 15px 20px;
  border-radius: 0 8px 8px 0;
  margin: 20px 0;

  p {
    margin: 0;
    color: #444;
    font-size: 14px;
  }

  strong {
    color: #1a1a1a;
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, #22C55E 0%, #16a34a 100%);
  color: white;
  padding: 25px;
  border-radius: 12px;
  text-align: center;

  h3 {
    margin-bottom: 15px;
    font-size: 1.2rem;
  }

  p {
    margin-bottom: 10px;
    font-size: 15px;
  }

  a {
    color: white;
    font-weight: 600;
  }
`;

const RelatedLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  flex-wrap: wrap;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #f0fdf4;
    color: #22C55E;
    padding: 12px 20px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      background: #22C55E;
      color: white;
    }
  }
`;

export const PrivacyPolicyPage = () => {
  return (
    <>
      <Navbar />
      <PageContainer>
        <ContentWrapper>
          <Breadcrumbs>
            <Link to="/">Inicio</Link>
            <span>›</span>
            <span>Política de Privacidad - PlantSnap AI</span>
          </Breadcrumbs>

          <Header>
            <AppBadge>
              🌿 PlantSnap AI
            </AppBadge>
            <Title>Política de Privacidad</Title>
            <LastUpdated>Última actualización: 24 de enero de 2026</LastUpdated>
          </Header>

          <Card>
            <TableOfContents>
              <h3>📋 Tabla de Contenidos</h3>
              <ol>
                <li><a href="#introduccion">Introducción</a></li>
                <li><a href="#datos-recopilados">Datos que Recopilamos</a></li>
                <li><a href="#uso-datos">Cómo Usamos sus Datos</a></li>
                <li><a href="#terceros">Terceros y Compartición de Datos</a></li>
                <li><a href="#almacenamiento">Almacenamiento y Seguridad</a></li>
                <li><a href="#derechos">Sus Derechos</a></li>
                <li><a href="#menores">Privacidad de Menores</a></li>
                <li><a href="#cookies">Cookies y Tecnologías Similares</a></li>
                <li><a href="#internacional">Transferencias Internacionales</a></li>
                <li><a href="#cambios">Cambios a esta Política</a></li>
                <li><a href="#contacto">Contacto</a></li>
              </ol>
            </TableOfContents>

            <Section id="introduccion">
              <SectionTitle>
                <span className="icon">1.</span> Introducción
              </SectionTitle>
              <Paragraph>
                Bienvenido a PlantSnap AI, una aplicación desarrollada y operada por <strong>Monaco IT Services</strong> 
                (en adelante, "nosotros", "nos" o "la Empresa"), con domicilio en Chile. Esta Política de Privacidad 
                describe cómo recopilamos, usamos, almacenamos y protegemos su información personal cuando utiliza 
                nuestra aplicación móvil PlantSnap AI (Bundle ID: com.martinseguel.plantsnapid), disponible para 
                iOS y Android.
              </Paragraph>
              <Paragraph>
                Al descargar, instalar o utilizar PlantSnap AI, usted acepta las prácticas descritas en esta 
                Política de Privacidad. Si no está de acuerdo con estas prácticas, le recomendamos no utilizar 
                nuestra aplicación.
              </Paragraph>
              <HighlightBox>
                <p>
                  <strong>🌱 Nuestro compromiso:</strong> Nos comprometemos a proteger su privacidad y a ser 
                  transparentes sobre cómo manejamos sus datos. Solo recopilamos la información necesaria para 
                  proporcionar y mejorar nuestros servicios.
                </p>
              </HighlightBox>
            </Section>

            <Section id="datos-recopilados">
              <SectionTitle>
                <span className="icon">2.</span> Datos que Recopilamos
              </SectionTitle>
              
              <Paragraph>
                Recopilamos diferentes tipos de información para proporcionar y mejorar nuestro servicio:
              </Paragraph>

              <DataTable>
                <table>
                  <thead>
                    <tr>
                      <th>Categoría</th>
                      <th>Datos Específicos</th>
                      <th>Propósito</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Datos de Cuenta</strong></td>
                      <td>Email, Nombre (opcional), ID de usuario</td>
                      <td>Autenticación y gestión de cuenta</td>
                    </tr>
                    <tr>
                      <td><strong>Fotos de Plantas</strong></td>
                      <td>Imágenes capturadas o subidas por el usuario</td>
                      <td>Identificación de plantas mediante IA</td>
                    </tr>
                    <tr>
                      <td><strong>Datos de Uso</strong></td>
                      <td>Historial de identificaciones, créditos usados, plantas guardadas</td>
                      <td>Funcionalidad del jardín virtual y seguimiento de uso</td>
                    </tr>
                    <tr>
                      <td><strong>Datos de Suscripción</strong></td>
                      <td>Estado de suscripción, historial de compras</td>
                      <td>Gestión de membresía Pro</td>
                    </tr>
                    <tr>
                      <td><strong>Notificaciones Push</strong></td>
                      <td>Token de dispositivo, preferencias de notificación</td>
                      <td>Envío de notificaciones relevantes</td>
                    </tr>
                    <tr>
                      <td><strong>Analytics</strong></td>
                      <td>Eventos de uso anónimos</td>
                      <td>Mejora de la aplicación (opcional)</td>
                    </tr>
                  </tbody>
                </table>
              </DataTable>

              <HighlightBox variant="warning">
                <p>
                  <strong>⚠️ Datos que NO recopilamos:</strong> No recopilamos su ubicación GPS, contactos, 
                  audio/micrófono ni datos biométricos. Face ID/Touch ID se procesan localmente en su 
                  dispositivo y nunca accedemos a estos datos.
                </p>
              </HighlightBox>
            </Section>

            <Section id="uso-datos">
              <SectionTitle>
                <span className="icon">3.</span> Cómo Usamos sus Datos
              </SectionTitle>
              <Paragraph>
                Utilizamos la información recopilada para los siguientes propósitos:
              </Paragraph>
              <List>
                <li><strong>Proporcionar el servicio:</strong> Identificar plantas a través de análisis de imágenes con IA, mostrar resultados y mantener su historial.</li>
                <li><strong>Gestionar su cuenta:</strong> Autenticar su identidad, mantener su jardín virtual y sincronizar datos entre dispositivos.</li>
                <li><strong>Procesar pagos:</strong> Gestionar su suscripción Pro y verificar el estado de sus créditos.</li>
                <li><strong>Enviar notificaciones:</strong> Informarle sobre sus plantas, recordatorios de cuidado y actualizaciones importantes.</li>
                <li><strong>Mejorar la aplicación:</strong> Analizar patrones de uso anónimos para optimizar la experiencia del usuario.</li>
                <li><strong>Soporte al cliente:</strong> Responder a sus consultas y resolver problemas técnicos.</li>
                <li><strong>Cumplimiento legal:</strong> Cumplir con obligaciones legales y proteger nuestros derechos.</li>
              </List>
            </Section>

            <Section id="terceros">
              <SectionTitle>
                <span className="icon">4.</span> Terceros y Compartición de Datos
              </SectionTitle>
              <Paragraph>
                Para proporcionar nuestros servicios, trabajamos con los siguientes proveedores de confianza:
              </Paragraph>
              <DataTable>
                <table>
                  <thead>
                    <tr>
                      <th>Proveedor</th>
                      <th>Propósito</th>
                      <th>Datos Compartidos</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>OpenAI</strong></td>
                      <td>Análisis de imágenes con IA</td>
                      <td>Fotos de plantas para identificación</td>
                    </tr>
                    <tr>
                      <td><strong>Clerk</strong></td>
                      <td>Autenticación de usuarios</td>
                      <td>Email, nombre, datos de sesión</td>
                    </tr>
                    <tr>
                      <td><strong>RevenueCat</strong></td>
                      <td>Gestión de suscripciones</td>
                      <td>ID de usuario, estado de suscripción</td>
                    </tr>
                    <tr>
                      <td><strong>Cloudflare R2</strong></td>
                      <td>Almacenamiento de imágenes</td>
                      <td>Fotos de plantas del historial</td>
                    </tr>
                    <tr>
                      <td><strong>Expo</strong></td>
                      <td>Notificaciones push</td>
                      <td>Token de dispositivo</td>
                    </tr>
                    <tr>
                      <td><strong>Railway</strong></td>
                      <td>Hosting del backend</td>
                      <td>Datos procesados por la aplicación</td>
                    </tr>
                  </tbody>
                </table>
              </DataTable>
              <Paragraph>
                Cada uno de estos proveedores está obligado contractualmente a proteger sus datos y a utilizarlos 
                únicamente para los fines especificados. No vendemos ni alquilamos su información personal a terceros.
              </Paragraph>
            </Section>

            <Section id="almacenamiento">
              <SectionTitle>
                <span className="icon">5.</span> Almacenamiento y Seguridad
              </SectionTitle>
              <Paragraph>
                Implementamos medidas de seguridad técnicas y organizativas para proteger su información:
              </Paragraph>
              <List>
                <li><strong>Encriptación:</strong> Todas las comunicaciones utilizan HTTPS/TLS.</li>
                <li><strong>Almacenamiento seguro:</strong> Sus imágenes se almacenan en Cloudflare R2 con acceso restringido.</li>
                <li><strong>Autenticación segura:</strong> Utilizamos Clerk para gestión de identidades con estándares de seguridad de la industria.</li>
                <li><strong>Acceso limitado:</strong> Solo el personal autorizado tiene acceso a los datos de usuario.</li>
                <li><strong>Retención de datos:</strong> Mantenemos sus datos mientras su cuenta esté activa o según sea necesario para proporcionar servicios.</li>
              </List>
              <HighlightBox>
                <p>
                  <strong>📍 Ubicación de datos:</strong> Sus datos pueden ser procesados y almacenados en 
                  servidores ubicados fuera de Chile, incluyendo Estados Unidos y la Unión Europea. Al usar 
                  nuestra aplicación, consiente a estas transferencias internacionales.
                </p>
              </HighlightBox>
            </Section>

            <Section id="derechos">
              <SectionTitle>
                <span className="icon">6.</span> Sus Derechos
              </SectionTitle>
              <Paragraph>
                Dependiendo de su ubicación, puede tener los siguientes derechos sobre sus datos personales:
              </Paragraph>
              
              <List>
                <li><strong>Derecho de acceso:</strong> Solicitar una copia de sus datos personales.</li>
                <li><strong>Derecho de rectificación:</strong> Corregir datos inexactos o incompletos.</li>
                <li><strong>Derecho de eliminación:</strong> Solicitar la eliminación de su cuenta y todos sus datos.</li>
                <li><strong>Derecho de portabilidad:</strong> Exportar sus datos en un formato estructurado.</li>
                <li><strong>Derecho de oposición:</strong> Oponerse al procesamiento de sus datos para ciertos fines.</li>
                <li><strong>Derecho a retirar consentimiento:</strong> Retirar su consentimiento en cualquier momento.</li>
              </List>

              <Paragraph>
                <strong>Para ejercer estos derechos:</strong>
              </Paragraph>
              <List>
                <li><strong>Eliminar cuenta:</strong> Vaya a Configuración &gt; Cuenta &gt; Eliminar cuenta en la app.</li>
                <li><strong>Exportar datos:</strong> Contacte a support@plantsnap.app para solicitar una exportación.</li>
                <li><strong>Desactivar notificaciones:</strong> Vaya a Configuración &gt; Notificaciones en la app.</li>
                <li><strong>Cancelar suscripción:</strong> A través de App Store o Google Play Store.</li>
              </List>

              <HighlightBox>
                <p>
                  <strong>🇪🇺 Usuarios de la UE (GDPR):</strong> Tiene derechos adicionales bajo el GDPR. 
                  Puede presentar una queja ante una autoridad de protección de datos si considera que 
                  hemos violado sus derechos.
                </p>
              </HighlightBox>
              <HighlightBox>
                <p>
                  <strong>🇺🇸 Residentes de California (CCPA):</strong> Tiene derecho a conocer qué información 
                  personal recopilamos, solicitar su eliminación y no ser discriminado por ejercer sus derechos.
                </p>
              </HighlightBox>
              <HighlightBox>
                <p>
                  <strong>🇨🇱 Usuarios en Chile:</strong> Sus derechos están protegidos bajo la Ley 19.628 
                  sobre Protección de la Vida Privada y sus modificaciones.
                </p>
              </HighlightBox>
            </Section>

            <Section id="menores">
              <SectionTitle>
                <span className="icon">7.</span> Privacidad de Menores
              </SectionTitle>
              <Paragraph>
                PlantSnap AI no está dirigida a menores de 13 años (o 16 años en jurisdicciones donde aplique). 
                No recopilamos intencionalmente información personal de niños menores de estas edades.
              </Paragraph>
              <Paragraph>
                Si usted es padre o tutor y cree que su hijo nos ha proporcionado información personal, 
                contáctenos inmediatamente a support@plantsnap.app y tomaremos las medidas necesarias para 
                eliminar dicha información.
              </Paragraph>
            </Section>

            <Section id="cookies">
              <SectionTitle>
                <span className="icon">8.</span> Cookies y Tecnologías Similares
              </SectionTitle>
              <Paragraph>
                Nuestra aplicación móvil no utiliza cookies tradicionales, pero puede emplear tecnologías 
                similares para:
              </Paragraph>
              <List>
                <li>Mantener su sesión activa</li>
                <li>Recordar sus preferencias</li>
                <li>Recopilar datos de analytics anónimos (si está habilitado)</li>
              </List>
              <Paragraph>
                Puede gestionar estas preferencias en la configuración de la aplicación.
              </Paragraph>
            </Section>

            <Section id="internacional">
              <SectionTitle>
                <span className="icon">9.</span> Transferencias Internacionales
              </SectionTitle>
              <Paragraph>
                Como utilizamos proveedores de servicios globales, sus datos pueden ser transferidos y 
                procesados en países fuera de su país de residencia. Tomamos medidas para garantizar que 
                estas transferencias cumplan con las leyes aplicables, incluyendo:
              </Paragraph>
              <List>
                <li>Cláusulas contractuales estándar aprobadas por la Comisión Europea</li>
                <li>Certificaciones de privacidad de nuestros proveedores</li>
                <li>Acuerdos de procesamiento de datos con todos los terceros</li>
              </List>
            </Section>

            <Section id="cambios">
              <SectionTitle>
                <span className="icon">10.</span> Cambios a esta Política
              </SectionTitle>
              <Paragraph>
                Podemos actualizar esta Política de Privacidad ocasionalmente. Le notificaremos sobre cambios 
                significativos mediante:
              </Paragraph>
              <List>
                <li>Un aviso dentro de la aplicación</li>
                <li>Un email a la dirección asociada con su cuenta</li>
                <li>Actualización de la fecha de "Última actualización" en esta página</li>
              </List>
              <Paragraph>
                Le recomendamos revisar esta política periódicamente. El uso continuado de la aplicación 
                después de los cambios constituye su aceptación de la política actualizada.
              </Paragraph>
            </Section>

            <Section id="contacto">
              <SectionTitle>
                <span className="icon">11.</span> Contacto
              </SectionTitle>
              <Paragraph>
                Si tiene preguntas sobre esta Política de Privacidad o sobre cómo manejamos sus datos, 
                puede contactarnos:
              </Paragraph>
              <ContactCard>
                <h3>Monaco IT Services</h3>
                <p>📧 Email: <a href="mailto:support@plantsnap.app">support@plantsnap.app</a></p>
                <p>🌐 Sitio web: <a href="https://monaco-it-services.cl" target="_blank" rel="noopener noreferrer">monaco-it-services.cl</a></p>
                <p>📍 País: Chile</p>
              </ContactCard>
            </Section>

            <RelatedLinks>
              <Link to="/plantsnapai/terms">
                📄 Términos de Servicio
              </Link>
              <Link to="/">
                🏠 Volver al Inicio
              </Link>
            </RelatedLinks>
          </Card>
        </ContentWrapper>
      </PageContainer>
      <Footer />
    </>
  );
};
